/**
 * A linguagem do painel — os primitivos que fazem uma tela do Hugi parecer uma tela do Hugi.
 *
 * Nasceu em 2026-09-03, depois de o owner medir a distância entre a prévia aprovada
 * (`docs/previas/painel-denso.html`) e o que subiu: *"sem ícones, sem fluxos claros definidos, um
 * monte de caixa espalhada pelas telas"*. As regras estão em `docs/mercado/bench-de-telas.md` §3, e
 * cada uma tem um primitivo aqui:
 *
 * - `Cabecalho` — toda tela abre com ladrilho da área, título, UMA frase e a ação primária à direita;
 * - `Ladrilho` — ícone num quadrado colorido; a cor é da área/canal, e o rótulo vem sempre ao lado;
 * - `Atalho` — ladrilho + nome + estado numa linha, clicável. É o "onde ir agora";
 * - `Indicador` — número grande, rótulo curto, nota de uma linha; âmbar quando pede ação;
 * - `Retrato` — a inicial de uma pessoa, no lugar de um uuid.
 *
 * **As cores dos ladrilhos moram no `vidro.css`** (`.ladrilho.<tom>`), porque este arquivo não pode
 * conter hexadecimal (catraca de palheta) e porque os degradês foram MEDIDOS lá: a ponta mais clara
 * de cada um segura o ícone branco em ≥ 3:1 (prévia, 03/09).
 */

import type { ReactNode } from "react";

/** Os tons existentes. Cada um é uma classe no `vidro.css`; um tom novo entra lá primeiro. */
export type TomDoLadrilho =
  | "wa" // WhatsApp (API oficial)
  | "hugi" // API Hugi — a marca
  | "tg" // Telegram
  | "em" // e-mail
  | "sm" // SMS
  | "ia" // agente
  | "fx" // fluxo
  | "eq" // equipe
  | "rl" // relatórios
  | "ag" // agenda
  | "ct" // contatos
  | "cf" // ajustes
  | "in" // integrações
  | "ch" // chat da equipe
  | "neutro";

export function Ladrilho(props: { readonly tom: TomDoLadrilho; readonly children: ReactNode; readonly grande?: boolean }) {
  return (
    <span aria-hidden="true" className={`ladrilho ${props.tom}${props.grande ? " grande" : ""}`}>
      {props.children}
    </span>
  );
}

/**
 * O cabeçalho de tela. `acoes` é o lugar da ação primária — uma por tela, preenchida. `chip` é o
 * estado da tela inteira (o Agente desligado, o fluxo publicado), quando existe.
 */
export function Cabecalho(props: {
  readonly titulo: string;
  readonly frase?: ReactNode;
  readonly ladrilho?: { readonly tom: TomDoLadrilho; readonly icone: ReactNode };
  readonly chip?: ReactNode;
  readonly acoes?: ReactNode;
}) {
  return (
    <header className="cabeca">
      <div className="flex min-w-0 items-center gap-3">
        {props.ladrilho ? (
          <Ladrilho tom={props.ladrilho.tom} grande>
            {props.ladrilho.icone}
          </Ladrilho>
        ) : null}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-[19px] font-semibold leading-tight tracking-[-0.01em] text-[color:var(--tinta-forte)]">
              {props.titulo}
            </h1>
            {props.chip}
          </div>
          {props.frase ? <p className="mt-0.5 text-[13px] text-[color:var(--tinta-fraca)]">{props.frase}</p> : null}
        </div>
      </div>
      {/* Sem `shrink-0`, e ele NAO era redundante com o `flex-wrap` do `.cabeca`: os dois consertam
          coisas diferentes. O wrap faz as acoes descerem para a propria linha; o `shrink-0` fazia a
          base delas ser `max-content`, entao mesmo na linha de baixo elas nunca quebravam ENTRE SI —
          medido a 375px no funil: 831px de largura, vazando pela direita. `min-w-0` porque item de
          flex nao encolhe abaixo do conteudo sem ele. */}
      {props.acoes ? <div className="flex min-w-0 flex-wrap items-center gap-2">{props.acoes}</div> : null}
    </header>
  );
}

/**
 * Um atalho: ladrilho, nome e o estado atual, numa linha, clicável.
 *
 * `href` navega de verdade (serve para outro build, como `/atender`); `aoClicar` é para quem já tem
 * um roteador — o painel passa `navegar(...)` do wouter. Os dois juntos não fazem sentido.
 */
export function Atalho(props: {
  readonly tom: TomDoLadrilho;
  readonly icone: ReactNode;
  readonly nome: string;
  readonly estado?: ReactNode;
  readonly href?: string;
  readonly aoClicar?: () => void;
}) {
  const miolo = (
    <>
      <Ladrilho tom={props.tom}>{props.icone}</Ladrilho>
      <span className="min-w-0 flex-1">
        <b className="block truncate text-[13px] font-semibold text-[color:var(--tinta-forte)]">{props.nome}</b>
        {props.estado !== undefined ? (
          <span className="block truncate text-[11.5px] text-[color:var(--tinta-fraca)]">{props.estado}</span>
        ) : null}
      </span>
    </>
  );
  if (props.href) {
    return (
      <a href={props.href} className="atalho">
        {miolo}
      </a>
    );
  }
  return (
    <button type="button" onClick={props.aoClicar} className="atalho">
      {miolo}
    </button>
  );
}

/** A faixa de atalhos — a grade que os acomoda. */
export function Atalhos(props: { readonly children: ReactNode }) {
  return <div className="atalhos">{props.children}</div>;
}

/**
 * Um indicador: o número, o rótulo e uma nota. `alerta` pinta o número de âmbar — só o número que
 * pede ação, senão nenhum se destaca. `aoClicar` faz o cartão inteiro ser o botão.
 */
export function Indicador(props: {
  readonly rotulo: ReactNode;
  readonly valor: ReactNode;
  readonly nota?: ReactNode;
  readonly alerta?: boolean;
  readonly aoClicar?: () => void;
}) {
  const classe = `vidro indicador${props.alerta ? " alerta" : ""}${props.aoClicar ? " acionavel" : ""}`;
  const miolo = (
    <>
      <span className="rotulo">{props.rotulo}</span>
      <span className="valor">{props.valor}</span>
      {props.nota !== undefined ? <span className="nota">{props.nota}</span> : null}
    </>
  );
  if (props.aoClicar) {
    return (
      <button type="button" onClick={props.aoClicar} className={`${classe} text-left`}>
        {miolo}
      </button>
    );
  }
  return <div className={classe}>{miolo}</div>;
}

/** A grade dos indicadores. */
export function Indicadores(props: { readonly children: ReactNode }) {
  return <div className="grade-de-numeros">{props.children}</div>;
}

/**
 * A marca do Hugi como glifo de ladrilho — as duas pílulas, a barra e o ponto de presença, sem o
 * fundo (o ladrilho `hugi` é o fundo). Geometria de `web/ui/marca/icone.svg`. Serve para a API Hugi:
 * *"independente da tecnologia por baixo, vamos sempre chamar de API Hugi"* (ADR-0015).
 */
export function MarcaHugi(props: { readonly tamanho?: number }) {
  const t = props.tamanho ?? 19;
  return (
    <svg viewBox="0 0 256 256" width={t} height={t} aria-hidden="true">
      <rect x="62" y="48" width="40" height="120" rx="20" fill="currentColor" />
      <rect x="142" y="88" width="40" height="120" rx="20" fill="currentColor" />
      <rect x="86" y="108" width="72" height="40" rx="20" fill="currentColor" />
      <circle cx="190" cy="200" r="28" fill="var(--verde)" stroke="currentColor" strokeWidth="12" />
    </svg>
  );
}

/** A inicial de uma pessoa da equipe. Uuid nunca aparece na tela — quando não há nome, há isto. */
export function Retrato(props: { readonly nome: string; readonly grande?: boolean }) {
  const limpo = props.nome.trim();
  const letra = (limpo.split("").find((c) => /\p{L}/u.test(c)) ?? limpo.charAt(0) ?? "?").toUpperCase();
  return (
    <span aria-hidden="true" className={`retrato${props.grande ? " grande" : ""}`}>
      {letra || "?"}
    </span>
  );
}
